import InjectedComponent from '@/components/InjectedComponent';
import { downloadBlob } from '@/lib/utils';
import { Column, Form, Grid, Input, Row, Segment, TextArea } from '@/semantic-ui/components';
import { EducationalDocumentStore, UserStore } from '@/store';
import { Formik } from 'formik';
import { differenceWith, isEqual } from 'lodash';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ID, IEducationDocument, moment } from 'sol-data';
import { array, mixed, number, object, string } from 'yup';
import Breadcrumbs from '../Breadcrumbs';
import Bulletin from '../Bulletin';
import ButtonBar from '../ButtonBar';
import CreateEducationalDocumentModal from '../CreateEducationalDocumentModal';
import DistributionGroup, { CompletedEmployee } from '../DistributionGroup';
import DragDrop from '../DragDrop';
import { withNavigation, WithNavigationProps } from '../Route';
import style from './index.less';

const { Field } = Form;

export const MAX_FILE_SIZE = 15000000;

interface State {
  shouldOpenFinishModal: boolean;
  shouldOpenPreviewModal: boolean;
}

interface Injected {
  educationDocumentStore: EducationalDocumentStore;
  userStore: UserStore;
}

interface Props extends WithNavigationProps {
  documentId: string;
}

@inject('educationDocumentStore', 'userStore')
@observer
class CreateEducationalDocument extends InjectedComponent<Props, State, Injected> {
  state: State = {
    shouldOpenFinishModal: false,
    shouldOpenPreviewModal: false,
  };

  async componentDidMount() {
    const { educationDocumentStore } = this.injected;
    const { documentId } = this.props.params;

    educationDocumentStore.clearCurrent();

    if (documentId !== 'add') {
      await this.fetchData(documentId!);
    }
  }

  async fetchData(id: ID) {
    const { educationDocumentStore } = this.injected;
    await educationDocumentStore.fetch(id);
  }

  render() {
    const { educationDocumentStore, userStore } = this.injected;
    const { documentId } = this.props.params;
    const { errorMessage, isLoading, current: educationDocument } = educationDocumentStore;
    const { shouldOpenFinishModal } = this.state;
    let initialValues: IEducationDocument & { file: File } = {
      title: '',
      description: '',
      azureBlobName: '',
      departments: [],
      fileName: '',
      file: new File([], ''),
    };

    const { fullName } = userStore.current!;
    let createdBy = `${fullName} - ${moment().format('YYYY-MM-DD')}`;

    let completedEmployees: CompletedEmployee[] = [];

    if (educationDocument) {
      initialValues = {
        ...initialValues,
        title: educationDocument.title,
        description: educationDocument.description,
        azureBlobName: educationDocument.azureBlobName,
        departments: educationDocument.departments.map(({ id }) => ({
          id,
        })),
        fileName: educationDocument.fileName,
        file: educationDocument.file ?? new File([], ''),
      };
      completedEmployees = educationDocument.completedEmployees.map(
        ({ signedOn, employee: { id } }) => ({
          employeeId: id,
          completionDate: signedOn ?? '--',
        }),
      );
      createdBy = `${educationDocument.postedBy} - ${educationDocument.postedOn}`;
    }

    const handlePreviewClick = async (file: File) => {
      const pdfFile = file;
      downloadBlob(pdfFile, pdfFile.name);
      this.setState({
        shouldOpenPreviewModal: true,
      });
    };

    const closePreview = () => {
      this.setState({
        shouldOpenPreviewModal: false,
      });
    };

    const closeModal = () => {
      this.setState({
        shouldOpenFinishModal: false,
      });
    };

    return (
      <>
        <Segment basic padded="very">
          <Breadcrumbs
            linkTitle="Educational Documents"
            activeBreadcrumb={educationDocument?.title ?? 'New Document'}
            link={'/education'}
            title="Educational Documents"
          />
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={object().shape({
              title: string().required('Title is required'),
              description: string().required('Description is required'),
              file: mixed()
                .test(
                  'fileSize',
                  'A file is required before submitting',
                  (file?: File | null | undefined) => {
                    if (!file) return documentId !== 'add'; // Allow null/undefined for existing documents
                    return file.size > 0;
                  },
                )
                .test(
                  'fileSize',
                  'File size must be less than 15mb',
                  (file?: File | null | undefined) => {
                    if (!file) return documentId !== 'add'; // Allow null/undefined for existing documents
                    return file.size <= MAX_FILE_SIZE;
                  },
                )
                .test(
                  'fileType',
                  'Unsupported file format. Please select jpg, png, or pdf.',
                  (file?: File | null | undefined) => {
                    if (!file) return documentId !== 'add'; // Allow null/undefined for existing documents
                    return ['application/pdf'].includes(file.type);
                  },
                )
                .required(),
              fileName: string().required('File name is required').label('File Name'),
              departments: array()
                .of(
                  object().shape({
                    id: number().min(1).required('Department must be selected'),
                  }),
                )
                .min(1, 'You must select at least one department'),
            })}
            onSubmit={async (
              { file, title, description, fileName, departments },
              { setSubmitting },
            ) => {
              if (documentId !== 'add') {
                if (departments.length < initialValues.departments.length) {
                  educationDocumentStore.setError('You cannot remove departments');
                } else {
                  const modifiedDepartments = differenceWith(
                    departments,
                    initialValues.departments,
                    isEqual,
                  );
                  await educationDocumentStore.update({
                    id: documentId,
                    departments: modifiedDepartments,
                    description,
                    title,
                  });
                }
              } else {
                const readyFormData = new FormData();
                readyFormData.append('file', file);
                const upload = await educationDocumentStore.uploadEducationDocumentPDF(
                  readyFormData,
                );
                await educationDocumentStore.create({
                  title,
                  description,
                  azureBlobName: upload.azureBlobName,
                  fileName,
                  departments,
                });
              }
              setSubmitting(false);
              if (educationDocumentStore.errorMessage === undefined) {
                this.setState({
                  shouldOpenFinishModal: true,
                });
              }
            }}
            render={({
              handleSubmit,
              values,
              isSubmitting,
              handleChange,
              setFieldValue,
              touched,
              errors,
            }) => {
              const showTitleError = !!touched.title && !!errors.title;
              const showDescriptionError = !!touched.description && !!errors.description;
              const showDepartmentsError = !!touched.departments && !!errors.departments;
              const showFileError = !!touched.file && !!errors.file;
              const removeFile = documentId === 'add' ? setFieldValue : undefined;
              const totalEmployees = educationDocument?.departments.reduce(
                (current, { employees }) => (employees?.length ?? 0) + current,
                0,
              );

              return (
                <Grid>
                  <div
                    style={{
                      color:
                        educationDocument?.completedEmployees.length === totalEmployees
                          ? 'green'
                          : 'red',
                    }}
                  >
                    {totalEmployees &&
                      `${
                        educationDocument?.completedEmployees.length || 0
                      }/${totalEmployees} signed`}
                  </div>
                  <Row>
                    <Bulletin
                      errorMessage={errorMessage}
                      handleDismiss={() => educationDocumentStore.clearError()}
                      isLoading={isLoading}
                    />
                  </Row>
                  <Row>
                    <Column width={8}>
                      <Field>
                        <p className={style.form_headers}>Title</p>
                        <Input
                          name="title"
                          value={values.title}
                          placeholder="Title"
                          onChange={handleChange}
                          fluid
                        />
                        {showTitleError && <p className="error_class">{errors.title}</p>}
                      </Field>
                    </Column>
                    <Column width={8}>
                      <Field>
                        <p className={style.form_headers}>Created By</p>
                        <Input value={createdBy} placeholder={createdBy} readOnly fluid />
                      </Field>
                    </Column>
                  </Row>
                  <Row>
                    <Column width={8}>
                      <Field>
                        <p className={style.form_headers}>Description</p>
                        <Form>
                          <TextArea
                            name="description"
                            value={values.description}
                            placeholder="Details of document"
                            rows={15}
                            onChange={handleChange}
                            autoHeight
                            className={style.input}
                          />
                        </Form>
                        {showDescriptionError && (
                          <p className="error_class">{errors.description}</p>
                        )}
                      </Field>
                    </Column>
                    <Column width={8}>
                      <Field>
                        <p className={style.form_headers}>Upload Document</p>
                        <DragDrop
                          text="Drag and drop files"
                          dragText="Drop your PDF document here!"
                          files={values.file && values.file instanceof File ? [values.file] : []}
                          allowReplace={documentId === 'add'}
                          height={285}
                          onFileDrop={(files: File[]) => {
                            if (files?.[0]) {
                              setFieldValue('fileName', files[0].name);
                              setFieldValue('file', files[0]);
                            }
                          }}
                          onFileDelete={
                            documentId === 'add'
                              ? () => {
                                  setFieldValue('file', new File([], ''));
                                  setFieldValue('fileName', '');
                                }
                              : undefined
                          }
                        />
                        {showFileError && <p className="error_class">{errors.file}</p>}
                      </Field>
                    </Column>
                  </Row>
                  <Row>
                    <Column width={16}>
                      <p className={style.form_headers}>Distribution</p>
                      <p>Select departments/groups below to receive this document</p>
                      <DistributionGroup
                        handleDepartmentClick={setFieldValue}
                        selectedDepartments={values.departments}
                        completedEmployees={completedEmployees}
                      />
                      {showDepartmentsError && <p className="error_class">{errors.departments}</p>}
                    </Column>
                  </Row>
                  <Row>
                    <Column width={16}>
                      <ButtonBar
                        onSave={handleSubmit}
                        link="/education"
                        disabled={isSubmitting}
                        saveText={'Publish'}
                      />
                    </Column>
                  </Row>
                </Grid>
              );
            }}
          />
        </Segment>
        <CreateEducationalDocumentModal open={shouldOpenFinishModal} handleClose={closeModal} />
      </>
    );
  }
}

export default withNavigation<{}>(CreateEducationalDocument);
